"use client";

/**
 * Simplified Subscription Upgrade/Downgrade Modal
 *
 * This modal provides a streamlined flow for subscription changes:
 * 1. Shows plan details and pricing (from props)
 * 2. Checks wallet balance against plan price
 * 3. If sufficient funds, allows user to proceed
 * 4. Calls upgrade/downgrade API with planId and billingCycle
 *
 * No complex cost calculations - just use the plan price directly.
 * The backend determines upgrade vs downgrade based on current plan vs target plan.
 */

import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "@/store/store";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Wallet,
  ArrowUp,
  ArrowDown,
  AlertTriangle,
  CheckCircle,
  Loader2,
  DollarSign,
  Crown,
} from "lucide-react";
import {
  upgradeSubscriptionRequest,
  resetSubscriptionState,
} from "@/store/slices/subscription.slice";
import { WalletBalance } from "@/services/walletService";

interface SubscriptionUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetPlanId: string;
  targetPlanName: string;
  targetPlanPrice: number;
  billingCycle: "monthly" | "yearly";
  walletBalance: WalletBalance | null;
}

export const SubscriptionUpgradeModal: React.FC<
  SubscriptionUpgradeModalProps
> = ({
  isOpen,
  onClose,
  targetPlanId,
  targetPlanName,
  targetPlanPrice,
  billingCycle,
  walletBalance,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { upgradeLoading, upgradeError, currentPlan } = useSelector(
    (state: RootState) => state.subscription,
  );

  const [step, setStep] = useState<"confirm" | "processing" | "success">(
    "confirm",
  );

  const handleClose = () => {
    dispatch(resetSubscriptionState());
    setStep("confirm");
    onClose();
  };

  const isUpgrade = useMemo(() => {
    if (!currentPlan) return true;

    // Plan hierarchy (lower index = lower tier)
    const planHierarchy = ["free", "starter", "professional", "enterprise"];

    const currentIndex = planHierarchy.indexOf(currentPlan.id || "free");
    const targetIndex = planHierarchy.indexOf(targetPlanId);

    return targetIndex > currentIndex;
  }, [currentPlan, targetPlanId]);
  const planCost = targetPlanPrice;
  const hasInsufficientFunds = walletBalance
    ? walletBalance.balance < planCost
    : false;

  const handleConfirmChange = () => {
    setStep("processing");
    dispatch(
      upgradeSubscriptionRequest({
        planId: targetPlanId,
        billingCycle,
      }),
    );
  };

  useEffect(() => {
    if (!upgradeLoading && step === "processing" && !upgradeError) {
      setStep("success");
    }
  }, [upgradeLoading, step, upgradeError]);

  const renderConfirmStep = () => (
    <div className="space-y-6">
      {/* Plan Change Summary */}
      <div className="p-4 bg-muted/30 rounded-lg">
        <div className="flex items-center gap-3 mb-3">
          {isUpgrade ? (
            <ArrowUp className="h-5 w-5 text-green-600" />
          ) : (
            <ArrowDown className="h-5 w-5 text-orange-600" />
          )}
          <h3 className="font-semibold">
            {isUpgrade ? "Upgrade" : "Downgrade"} Confirmation
          </h3>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Current Plan:</span>
            <span className="font-medium">
              {currentPlan?.name || "Unknown"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">New Plan:</span>
            <span className="font-medium">{targetPlanName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Billing Cycle:</span>
            <span className="font-medium capitalize">{billingCycle}</span>
          </div>
        </div>
      </div>

      {/* Cost Information */}
      <div className="p-4 border rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">Plan Cost</h3>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Plan Price:</span>
            <span className="font-medium text-lg">
              ${planCost.toFixed(2)}/{billingCycle}
            </span>
          </div>
        </div>
      </div>

      {/* Wallet Balance Check */}
      {walletBalance && (
        <div className="p-4 border rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Wallet className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold">Wallet Balance</h3>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Available Balance:</span>
              <span className="font-medium">
                ${walletBalance.balance.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Required Amount:</span>
              <span className="font-medium">${planCost.toFixed(2)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t">
              <span className="text-muted-foreground">Balance After:</span>
              <span
                className={`font-medium ${hasInsufficientFunds ? "text-red-600" : "text-green-600"}`}
              >
                $
                {hasInsufficientFunds
                  ? "Insufficient"
                  : (walletBalance.balance - planCost).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Insufficient Funds Warning */}
      {hasInsufficientFunds && (
        <Alert className="border-red-200 bg-red-50 dark:bg-red-950/20">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800 dark:text-red-200">
            <strong>Insufficient wallet balance.</strong> You need $
            {planCost.toFixed(2)} but only have $
            {walletBalance?.balance.toFixed(2) || "0.00"} available. Please add
            funds to your wallet before proceeding.
          </AlertDescription>
        </Alert>
      )}

      {/* Error Display */}
      {upgradeError && (
        <Alert className="border-red-200 bg-red-50 dark:bg-red-950/20">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800 dark:text-red-200">
            {upgradeError}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );

  const renderProcessingStep = () => (
    <div className="text-center py-8">
      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
      <h3 className="text-lg font-semibold mb-2">
        Processing {isUpgrade ? "Upgrade" : "Downgrade"}
      </h3>
      <p className="text-muted-foreground">
        Please wait while we process your subscription change...
      </p>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="text-center py-8">
      <CheckCircle className="h-8 w-8 mx-auto mb-4 text-green-600" />
      <h3 className="text-lg font-semibold mb-2">
        {isUpgrade ? "Upgrade" : "Downgrade"} Successful!
      </h3>
      <p className="text-muted-foreground">
        Your subscription has been successfully updated to {targetPlanName}.
      </p>
      <div className="mt-4 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
        <div className="flex items-center gap-2 justify-center">
          <Crown className="h-4 w-4 text-green-600" />
          <span className="font-medium text-green-800 dark:text-green-200">
            Welcome to {targetPlanName}!
          </span>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (step) {
      case "confirm":
        return renderConfirmStep();
      case "processing":
        return renderProcessingStep();
      case "success":
        return renderSuccessStep();
      default:
        return renderConfirmStep();
    }
  };

  const getFooterButtons = () => {
    switch (step) {
      case "confirm":
        return (
          <>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmChange}
              disabled={hasInsufficientFunds || upgradeLoading}
              className={
                isUpgrade
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-orange-600 hover:bg-orange-700"
              }
            >
              {upgradeLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : isUpgrade ? (
                <ArrowUp className="h-4 w-4 mr-2" />
              ) : (
                <ArrowDown className="h-4 w-4 mr-2" />
              )}
              Confirm {isUpgrade ? "Upgrade" : "Downgrade"} ($
              {planCost.toFixed(2)})
            </Button>
          </>
        );
      case "processing":
        return (
          <Button variant="outline" disabled>
            Processing...
          </Button>
        );
      case "success":
        return <Button onClick={handleClose}>Close</Button>;
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isUpgrade ? (
              <ArrowUp className="h-5 w-5 text-green-600" />
            ) : (
              <ArrowDown className="h-5 w-5 text-orange-600" />
            )}
            {isUpgrade ? "Upgrade" : "Downgrade"} to {targetPlanName}
          </DialogTitle>
          <DialogDescription>
            {step === "confirm" &&
              "Please review the details before confirming your subscription change."}
            {step === "processing" && "Processing your subscription change..."}
            {step === "success" &&
              "Your subscription has been successfully updated."}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">{renderContent()}</div>

        <DialogFooter>{getFooterButtons()}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
